import { useState, useMemo } from 'react';
import {
  apiErrorText,
  useSearchFinCounterpartiesQuery,
  useCreateOrUpdateCounterpartyMutation,
  useUpdateFinCounterpartyMutation,
  useSync1CApiMutation,
  type CounterpartiesFilter,
  type FinCounterpartyRow,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconPlus, IconRefresh, IconX } from '@shared/ui/icons';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { useAgentPermission } from '@entities/user';
import {
  Container,
  HeaderRow,
  TitleBox,
  Title,
  Subtitle,
  ActionsGroup,
  ShownCaption,
  SyncToast,
} from './styled';
import { CounterpartyKpiGrid } from './CounterpartyKpiGrid';
import { CounterpartyFilterBar } from './CounterpartyFilterBar';
import { CounterpartyTable } from './CounterpartyTable';
import { CounterpartyModal } from './CounterpartyModal';
import {
  computeKpis,
  createPayload,
  matchesRequisites,
  shownCaption,
  sourceOptions,
  updatePayload,
  type CounterpartyForm,
  type RequisitesFilter,
} from './model';

export function CounterpartiesView() {
  const { can_write } = useAgentPermission('finance');
  const [sync1C, { isLoading: isSyncing }] = useSync1CApiMutation();
  const [createCounterparty, { isLoading: isCreating }] = useCreateOrUpdateCounterpartyMutation();
  const [updateCounterparty, { isLoading: isUpdating }] = useUpdateFinCounterpartyMutation();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [requisitesFilter, setRequisitesFilter] = useState<RequisitesFilter>('all');

  // Поиск, тип и источник отбирает сервер — среди всех карточек, а не загруженных
  const searchText = useDebouncedValue(search.trim(), 300);
  const serverFilter = useMemo<CounterpartiesFilter | undefined>(() => {
    const f: CounterpartiesFilter = {};
    if (searchText) f.search = searchText;
    if (typeFilter !== 'all') f.counterparty_type = typeFilter;
    if (sourceFilter !== 'all') f.source = sourceFilter;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [searchText, typeFilter, sourceFilter]);

  // Весь справочник — для показателей и списка источников; без отбора это тот же запрос
  const directory = useSearchFinCounterpartiesQuery();
  const listing = useSearchFinCounterpartiesQuery(serverFilter);

  const [syncResult, setSyncResult] = useState<{ success: boolean; text: string } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FinCounterpartyRow | null>(null);

  const openCreateModal = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const openEditModal = (item: FinCounterpartyRow) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleSync1C = async () => {
    try {
      setSyncResult(null);
      const res = await sync1C().unwrap();
      setSyncResult({
        success: true,
        text: `Синхронизация 1С завершена: ${res.message} (режим: ${res.connection_mode})`,
      });
    } catch (err: unknown) {
      setSyncResult({
        success: false,
        text: apiErrorText(err, 'Не удалось синхронизировать справочник с 1С.'),
      });
    }
  };

  // Правка — по id той же карточки, новая — отдельной записью; ошибка уходит в окно
  const handleSaveCounterparty = async (form: CounterpartyForm) => {
    if (editingItem) {
      await updateCounterparty({ id: editingItem.id, ...updatePayload(form) }).unwrap();
    } else {
      await createCounterparty(createPayload(form)).unwrap();
    }
  };

  const allRows = directory.data?.items;
  const kpis = useMemo(
    () => computeKpis(allRows ?? [], directory.data?.total ?? 0),
    [allRows, directory.data?.total],
  );
  const sourceChoices = useMemo(
    () => sourceOptions((allRows ?? []).map((c) => c.source)),
    [allRows],
  );

  const listed = listing.data?.items;
  const filtered = useMemo(
    () => (listed ?? []).filter((c) => matchesRequisites(c, requisitesFilter)),
    [listed, requisitesFilter],
  );
  const caption =
    listing.isFetching && !listing.isLoading
      ? 'Ищем в справочнике…'
      : listing.data
        ? shownCaption(listing.data.items.length, listing.data.total)
        : null;

  return (
    <Container>
      <HeaderRow>
        <TitleBox>
          <Title>Справочник контрагентов и интеграция с 1С</Title>
          <Subtitle>
            Единая база поставщиков, подрядчиков и банковских реквизитов. Автопополнение из 1С и выписок банка.
          </Subtitle>
        </TitleBox>
        <ActionsGroup>
          <Button
            $variant="secondary"
            onClick={handleSync1C}
            disabled={isSyncing || !can_write}
          >
            <IconRefresh size={16} />
            {isSyncing ? 'Синхронизация…' : 'Синхронизация с 1С'}
          </Button>
          <Button
            $variant="primary"
            onClick={openCreateModal}
            disabled={!can_write}
          >
            <IconPlus size={16} />
            Добавить контрагента
          </Button>
        </ActionsGroup>
      </HeaderRow>

      {syncResult && (
        <SyncToast $success={syncResult.success} role={syncResult.success ? 'status' : 'alert'}>
          <span>{syncResult.text}</span>
          <Button $variant="ghost" $size="sm" onClick={() => setSyncResult(null)} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </SyncToast>
      )}

      {/* KPI карточки */}
      <CounterpartyKpiGrid
        metrics={kpis}
        status={directory.data ? 'ready' : directory.isError ? 'error' : 'loading'}
      />

      {/* Фильтры */}
      <CounterpartyFilterBar
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        sourceFilter={sourceFilter}
        onSourceFilterChange={setSourceFilter}
        sourceChoices={sourceChoices}
        requisitesFilter={requisitesFilter}
        onRequisitesFilterChange={(v) => setRequisitesFilter(v as RequisitesFilter)}
      />

      {caption && <ShownCaption role="status">{caption}</ShownCaption>}

      {/* Таблица */}
      <CounterpartyTable
        counterparties={filtered}
        isLoading={listing.isLoading}
        isError={listing.isError}
        errorText={apiErrorText(listing.error, 'Не удалось загрузить контрагентов.')}
        refetch={listing.refetch}
        onEditItem={openEditModal}
        canWrite={Boolean(can_write)}
      />

      {/* Модалка редактирования / создания */}
      <CounterpartyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editingItem={editingItem}
        onSave={handleSaveCounterparty}
        isSaving={isCreating || isUpdating}
      />
    </Container>
  );
}
